import type { AppSettings, Invoice, Payment, Quote } from '../types';

export interface BackupPayload {
  version: 1 | 2 | 3;
  exportedAt?: string;
  quotes: Quote[];
  invoices: Invoice[];
  payments: Payment[];
  settings?: AppSettings;
}

export const MAX_BACKUP_BYTES = 100 * 1024 * 1024;

export const backupSizeBytes = (payload: BackupPayload) => new TextEncoder()
  .encode(JSON.stringify(payload)).byteLength;

export type BackupUsageLevel = 'normal' | 'caution' | 'warning' | 'danger';

export const backupUsageLevel = (bytes: number): BackupUsageLevel => {
  const percent = (Math.max(0, bytes) / MAX_BACKUP_BYTES) * 100;
  if (percent >= 95) return 'danger';
  if (percent >= 85) return 'warning';
  if (percent >= 70) return 'caution';
  return 'normal';
};

const unique = (values: string[]) => new Set(values.map((value) => value.trim().toLocaleLowerCase())).size === values.length;

const validQuote = (quote: Quote) => Boolean(
  quote && typeof quote.id === 'string' && quote.id && typeof quote.number === 'string' && quote.number.trim()
  && typeof quote.customerName === 'string' && Array.isArray(quote.items) && quote.items.length > 0
  && quote.items.every((item) => typeof item?.description === 'string' && Number.isFinite(Number(item.quantity))
    && Number(item.quantity) > 0 && Number.isFinite(Number(item.unitPrice)) && Number(item.unitPrice) >= 0),
);

export const parseBackup = (text: string): BackupPayload => {
  let raw: Partial<BackupPayload>;
  try { raw = JSON.parse(text) as Partial<BackupPayload>; }
  catch { throw new Error('ไฟล์สำรองไม่ใช่ JSON ที่ถูกต้อง'); }
  if (![1, 2, 3].includes(raw.version ?? 0) || !Array.isArray(raw.quotes)) throw new Error('ไฟล์นี้ไม่ใช่ข้อมูลสำรอง BilaFlow ที่รองรับ');
  if (!raw.quotes.every(validQuote) || !unique(raw.quotes.map((quote) => quote.id)) || !unique(raw.quotes.map((quote) => quote.number))) {
    throw new Error('ข้อมูลใบเสนอราคาไม่ครบหรือมีเลขเอกสารซ้ำ');
  }

  const invoices = Array.isArray(raw.invoices) ? raw.invoices : [];
  if (!invoices.every((invoice) => invoice && typeof invoice.id === 'string' && typeof invoice.number === 'string'
    && typeof invoice.quoteId === 'string' && validQuote(invoice.quoteSnapshot))
    || !unique(invoices.map((invoice) => invoice.id)) || !unique(invoices.map((invoice) => invoice.number))) {
    throw new Error('ข้อมูลใบแจ้งหนี้ไม่ครบหรือมีเลขเอกสารซ้ำ');
  }

  const invoiceIds = new Set(invoices.map((invoice) => invoice.id));
  const payments = Array.isArray(raw.payments) ? raw.payments : [];
  if (!payments.every((payment) => payment && typeof payment.id === 'string' && invoiceIds.has(payment.invoiceId)
    && typeof payment.receiptNumber === 'string' && payment.receiptNumber.trim()
    && Number.isFinite(Number(payment.amount)) && Number(payment.amount) > 0)
    || !unique(payments.map((payment) => payment.id)) || !unique(payments.map((payment) => payment.receiptNumber))) {
    throw new Error('ข้อมูลการรับชำระไม่ครบ มีเลขใบเสร็จซ้ำ หรือไม่สัมพันธ์กับใบแจ้งหนี้');
  }

  return { version: raw.version as 1 | 2 | 3, exportedAt: raw.exportedAt, quotes: raw.quotes, invoices, payments, settings: raw.settings };
};
